"use client";

import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { trackPopupClick, trackPopupDisplay } from "@/lib/analytics/ga-events";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";

const ExitIntentPopup = () => {
  const t = useTranslations("Articles.popups");
  const [isVisible, setIsVisible] = useState(false);
  const exitIntentFired = useRef(false);
  const hasTrackedDisplay = useRef(false);

  useEffect(() => {
    const handleMouseOut = (e: MouseEvent) => {
      if (isVisible) return;

      const fromElement = e.relatedTarget as HTMLElement;

      if (e.clientY <= 0 || fromElement === null || fromElement?.nodeName === "HTML") {
        setIsVisible(true);

        if (!hasTrackedDisplay.current) {
          trackPopupDisplay("exit_intent");
          hasTrackedDisplay.current = true;
        }

        exitIntentFired.current = true;
        setTimeout(() => {
          exitIntentFired.current = false;
        }, 2000);
      }
    };

    const exitIntentTimeout = setTimeout(() => {
      document.addEventListener("mouseout", handleMouseOut);
    }, 2000);

    return () => {
      clearTimeout(exitIntentTimeout);
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isVisible]);

  const handleClose = () => {
    trackPopupClick("exit_intent", "close");
    setIsVisible(false);
  };

  const handleCTAClick = () => {
    trackPopupClick("exit_intent", "cta_click");
    handleClose();
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleClose}
        onKeyDown={handleOverlayKeyDown}
        aria-label="Close popup overlay"
      />

      <div
        className="animate-in fade-in zoom-in fixed left-1/2 top-1/2 z-50 w-[90%] max-w-lg -translate-x-1/2 -translate-y-1/2 transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-title"
        aria-describedby="exit-description"
      >
        <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 transition-all duration-300 hover:bg-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Close popup"
            type="button"
          >
            <X className="h-5 w-5 text-gray-700" />
          </button>

          <div className="p-8 md:p-10">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-primary/10 p-4">
                <svg className="h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
              </div>
            </div>

            <h3 id="exit-title" className="urbanist mb-3 text-center text-2xl font-bold text-gray-900 md:text-3xl">
              {t("exitIntent.title")}
            </h3>
            <p id="exit-description" className="inter mb-8 text-center text-gray-600">
              {t("exitIntent.description")}
            </p>

            <Link href="/buy-used-cars-uae" onClick={handleCTAClick}>
              <button
                className="w-full rounded-xl bg-primary px-6 py-4 font-urbanist font-semibold text-white transition-all duration-300 hover:bg-primary-dark hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 active:scale-95"
                type="button"
              >
                {t("exitIntent.cta")}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExitIntentPopup;
